// Local-only debug log for the PowerPoint add-in. Entries live in localStorage (capped) so they
// survive the add-in reloading, and are shown on /debug/powerpoint. Nothing leaves the browser.

export type DebugLogType = 'route' | 'browser' | 'office' | 'idb' | 'info' | 'error';

export interface DebugLogEntry {
  id: number;
  ts: number;
  type: DebugLogType;
  event: string;
  data?: Record<string, unknown>;
}

export const DEBUG_LOG_MAX = 500;
export const DEBUG_LOG_KEY = 'sa_debug_logs';
const CHANGE_EVENT = 'sa-debug-log-change';

export const getDebugLogs = (): DebugLogEntry[] => {
  try {
    const raw = window.localStorage.getItem(DEBUG_LOG_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const write = (logs: DebugLogEntry[]) => {
  try {
    window.localStorage.setItem(DEBUG_LOG_KEY, JSON.stringify(logs));
  } catch {
    // storage full or unavailable: drop the entry rather than break the app
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
};

const safe = (data?: Record<string, unknown>) => {
  if (!data) return undefined;
  try {
    return JSON.parse(JSON.stringify(data));
  } catch {
    return { note: 'unserializable data' };
  }
};

// Always records (not gated on the flag) so logs already exist when someone turns the flag on.
export const debugLog = (type: DebugLogType, event: string, data?: Record<string, unknown>) => {
  if (typeof window === 'undefined') return;
  try {
    const logs = getDebugLogs();
    const last = logs[logs.length - 1];
    logs.push({ id: (last?.id ?? 0) + 1, ts: Date.now(), type, event, data: safe(data) });
    write(logs.slice(-DEBUG_LOG_MAX));
  } catch {
    // never let logging throw
  }
};

export const clearDebugLogs = () => write([]);

export const subscribeDebugLogs = (cb: () => void) => {
  window.addEventListener(CHANGE_EVENT, cb);
  window.addEventListener('storage', cb);
  return () => {
    window.removeEventListener(CHANGE_EVENT, cb);
    window.removeEventListener('storage', cb);
  };
};

// Opens an IndexedDB database (no version, so it can never upgrade or alter it) and logs the
// outcome: opened / blocked / error / hung. A hang here is the Firestore storage-lock stall.
export const probeIndexedDb = (name = 'firebaseLocalStorageDb', timeoutMs = 3000): Promise<string> =>
  new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      debugLog('idb', 'unavailable', { name });
      return resolve('unavailable');
    }
    const start = Date.now();
    let done = false;
    const finish = (event: string, extra?: Record<string, unknown>) => {
      if (done && event !== 'opened late') return;
      done = true;
      debugLog(event === 'hung' || event === 'blocked' || event === 'error' ? 'error' : 'idb', `${name} ${event}`, {
        name,
        ms: Date.now() - start,
        ...extra
      });
      resolve(event);
    };
    try {
      const req = indexedDB.open(name);
      req.onsuccess = () => {
        req.result.close();
        finish(Date.now() - start > timeoutMs ? 'opened late' : 'opened');
      };
      req.onblocked = () => finish('blocked');
      req.onerror = () => finish('error', { error: String(req.error) });
      setTimeout(() => finish('hung', { timeoutMs }), timeoutMs);
    } catch (error) {
      finish('error', { error: String(error) });
    }
  });
